import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Grid,
  Skeleton,
  Button,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import PromotionHero from "./PromotionHero";
import axios from "axios";
import { getAuthCredentials } from "../../utils/auth";
import UploadHome from "./Functions/uploadHome";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const moveItem = (arr, from, to) => {
  const copy = arr.slice();
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};

const MediaCard = ({
  item,
  index,
  arrange,
  onDragStart,
  onDragOver,
  onDrop,
  onChangeOrderNumber,
  onOpenEdit,
}) => {
  const isVideo = item?.type === "video";
  const isGif = item?.type === "gif";
  const isImage = item?.type === "image";

  return (
    <Box
      draggable={arrange}
      onDragStart={(e) => arrange && onDragStart(e, index)}
      onDragOver={(e) => arrange && onDragOver(e, index)}
      onDrop={(e) => arrange && onDrop(e, index)}
      onClick={() => !arrange && onOpenEdit?.(index, item)}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        transition: "box-shadow .15s, transform .02s",
        cursor: arrange ? "grab" : "pointer",
        "&:hover": { boxShadow: 2 },
        "&:active": arrange ? { transform: "scale(.997)" } : undefined,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          position: "absolute",
          top: 8,
          left: 8,
          zIndex: 2,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.8),
          borderRadius: 1,
          p: 0.5,
          pr: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {arrange ? (
          <TextField
            type="number"
            size="small"
            value={index + 1}
            onChange={(e) => {
              const v = parseInt(e.target.value || "1", 10);
              onChangeOrderNumber(index, v);
            }}
            inputProps={{ min: 1, style: { width: 48, textAlign: "center" } }}
          />
        ) : (
          <Chip size="small" label={`#${index + 1}`} />
        )}
        {arrange && (
          <Tooltip title="Drag to reorder">
            <IconButton size="small">
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Box
        sx={{
          width: "100%",
          aspectRatio: "9 / 16",
          display: "grid",
          placeItems: "center",
          bgcolor: isVideo ? "#000" : "#fff",
        }}
      >
        {isVideo ? (
          <Box
            component="video"
            src={item.url}
            controls
            muted
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : isImage || isGif ? (
          <Box
            component="img"
            src={item.url}
            alt={item.alt || ""}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "";
            }}
          />
        ) : (
          <Stack alignItems="center" color="text.secondary">
            <BrokenImageIcon />
            <Typography variant="caption">Unknown media</Typography>
          </Stack>
        )}
      </Box>

      <Box sx={{ p: 1.25 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          noWrap
          title={item.title || item.alt || item.url}
        >
          {item.title || item.alt || item.url}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {item.type?.toUpperCase()} • {item.isActive ? "Active" : "Inactive"}
        </Typography>
      </Box>
    </Box>
  );
};

const PromoHome = () => {
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState([]);
  const [indexOrder, setIndexOrder] = useState([]);

  const [arrange, setArrange] = useState(false);
  const [adding, setAdding] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editId, setEditId] = useState(null);

  const persistTimerRef = useRef(null);
  const dragSrcRef = useRef(null);

  const fetchHome = useCallback(async () => {
    try {
      setLoading(true);
      const { token } = await getAuthCredentials();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/fetch-home`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = Array.isArray(res.data.media) ? res.data.media : [];
      const sorted = list
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setMedia(sorted);
      setIndexOrder(sorted.map((_, i) => i));
    } catch {
      setMedia([]);
      setIndexOrder([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHome();
  }, [fetchHome]);

  const schedulePersist = useCallback(async (nextOrder) => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(async () => {
      try {
        const { token } = await getAuthCredentials();
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/home/arrange`,
          { order: nextOrder },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch {}
    }, 700);
  }, []);

  const handleDragStart = (e, idx) => {
    dragSrcRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (_e, destIdx) => {
    const srcIdx = dragSrcRef.current;
    if (srcIdx == null || destIdx == null || srcIdx === destIdx) return;
    const newMedia = moveItem(media, srcIdx, destIdx);
    const newOrder = moveItem(indexOrder, srcIdx, destIdx);
    setMedia(newMedia);
    setIndexOrder(newOrder);
    schedulePersist(newOrder);
  };

  const handleChangeOrderNumber = (fromIdx, desiredOneBased) => {
    const toIdx = clamp((desiredOneBased || 1) - 1, 0, media.length - 1);
    if (toIdx === fromIdx) return;
    const newMedia = moveItem(media, fromIdx, toIdx);
    const newOrder = moveItem(indexOrder, fromIdx, toIdx);
    setMedia(newMedia);
    setIndexOrder(newOrder);
    schedulePersist(newOrder);
  };

  const openEditFor = (idx, item) => {
    if (arrange) return;
    setEditIndex(idx);
    setEditItem(item);
    setEditId(item?._id);
    setEditOpen(true);
  };

  const headerActions = useMemo(
    () => (
      <Stack direction="row" spacing={1}>
        <Button
          variant={arrange ? "contained" : "outlined"}
          startIcon={<SwapVertIcon />}
          onClick={() => setArrange((v) => !v)}
        >
          {arrange ? "Done" : "Arrange"}
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAdding(true)}
        >
          Add Media
        </Button>
      </Stack>
    ),
    [arrange]
  );

  return (
    <Box sx={{ p: 3 }}>
      <PromotionHero title="Promotions — Home Page" />

      <Paper elevation={3} sx={{ mt: -2, borderRadius: 3, p: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          mb={2}
        >
          <Typography variant="h6" fontWeight={700}>
            Home Page Media
          </Typography>
          {headerActions}
        </Stack>

        {arrange && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: "block" }}
          >
            Drag cards or edit the number badge to reorder. Changes save
            automatically.
          </Typography>
        )}

        {loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rounded" height={320} />
              </Grid>
            ))}
          </Grid>
        ) : media.length === 0 ? (
          <Stack alignItems="center" sx={{ py: 6, color: "text.secondary" }}>
            <BrokenImageIcon />
            <Typography variant="body2">Nothing found</Typography>
          </Stack>
        ) : (
          <Grid container spacing={2}>
            {media.map((m, idx) => (
              <Grid item xs={12} sm={6} md={4} key={`${m.url}-${idx}`}>
                <MediaCard
                  item={m}
                  index={idx}
                  arrange={arrange}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onChangeOrderNumber={handleChangeOrderNumber}
                  onOpenEdit={openEditFor}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Add */}
      <UploadHome
        open={adding}
        onClose={() => setAdding(false)}
        onSuccess={fetchHome}
        existingItem={null}
        editIndex={null}
        editId={null}
        key={adding ? "add-open" : "add-closed"}
      />

      {/* Edit — key forces a fresh mount per selection */}
      <UploadHome
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSuccess={fetchHome}
        existingItem={editItem}
        editIndex={editIndex}
        editId={editId}
        key={
          editOpen ? `edit-${editIndex}-${editItem?.url || ""}` : "edit-closed"
        }
      />
    </Box>
  );
};

export default PromoHome;
